import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { API_BASE_URL } from '../../config/api';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
`;

const Title = styled.h2`
  color: #fff;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #a0aec0;
  margin-bottom: 2rem;
  text-align: center;
`;

const CodeInputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const CodeInput = styled.input`
  width: 50px;
  height: 60px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: bold;
  border: 2px solid rgba(139, 92, 246, 0.3);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  
  &:focus {
    outline: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.2);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'link' }>`
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => {
    if (props.variant === 'link') {
      return `
        background: transparent;
        color: #8b5cf6;
        padding: 0.5rem;
        text-decoration: underline;
        
        &:hover {
          color: #a78bfa;
        }
      `;
    }
    if (props.variant === 'secondary') {
      return `
        background: transparent;
        border: 1px solid rgba(139, 92, 246, 0.5);
        color: #8b5cf6;
        
        &:hover {
          background: rgba(139, 92, 246, 0.1);
        }
      `;
    }
    return `
      background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
      color: white;
      min-width: 200px;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
      }
    `;
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #ef4444;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

const BackupCodeInput = styled.input`
  width: 100%;
  max-width: 300px;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 1rem;
  text-align: center;
  letter-spacing: 2px;
  margin-bottom: 1rem;
  
  &:focus {
    outline: none;
    border-color: #8b5cf6;
  }
`;

const ToggleMode = styled.button`
  background: none;
  border: none;
  color: #8b5cf6;
  cursor: pointer;
  margin-top: 1rem;
  font-size: 0.9rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

interface MfaVerificationProps {
  mfaToken: string;
  onSuccess: (response: any) => void;
  onCancel: () => void;
}

const MfaVerification: React.FC<MfaVerificationProps> = ({ mfaToken, onSuccess, onCancel }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [backupCode, setBackupCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus();
  }, []);

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);
    
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    setError('');
    
    // Auto-focus next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all digits are entered
    if (digit && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        verifyCode(fullCode);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
      verifyCode(pastedData);
    }
  };

  const verifyCode = async (codeToVerify?: string) => {
    const mfaCode = codeToVerify || (useBackupCode ? backupCode.replace(/-/g, '') : code.join(''));
    
    if (!useBackupCode && mfaCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    
    if (useBackupCode && mfaCode.length < 8) {
      setError('Please enter a valid backup code');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/mfa/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          mfaToken,
          mfaCode
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Invalid verification code');
      }
      
      onSuccess(data);
    } catch (err: any) {
      setError(err.message);
      // Clear code on error
      if (!useBackupCode) {
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Title>🔐 Two-Factor Authentication</Title>
      <Subtitle>
        {useBackupCode
          ? 'Enter one of your backup codes'
          : 'Enter the 6-digit code from your authenticator app'
        }
      </Subtitle>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      {useBackupCode ? (
        <>
          <BackupCodeInput
            type="text"
            value={backupCode}
            onChange={(e) => {
              setBackupCode(e.target.value.toUpperCase());
              setError('');
            }}
            placeholder="XXXX-XXXX"
            maxLength={9}
            autoFocus
          />
          <Button onClick={() => verifyCode()} disabled={loading || backupCode.length < 8}>
            {loading ? 'Verifying...' : 'Verify Backup Code'}
          </Button>
        </>
      ) : (
        <>
          <CodeInputContainer onPaste={handlePaste}>
            {code.map((digit, index) => (
              <CodeInput
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                maxLength={1}
                placeholder="•"
              />
            ))}
          </CodeInputContainer>
          <Button onClick={() => verifyCode()} disabled={loading || code.join('').length !== 6}>
            {loading ? 'Verifying...' : 'Verify'}
          </Button>
        </>
      )}
      
      <ToggleMode onClick={() => {
        setUseBackupCode(!useBackupCode);
        setError('');
      }}>
        {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code instead'}
      </ToggleMode>
      
      <Button variant="link" onClick={onCancel} style={{ marginTop: '1rem' }}>
        Cancel and go back
      </Button>
    </Container>
  );
};

export default MfaVerification;
